import React, { useState, useCallback, useEffect, useMemo } from "react";

import Divider from "@mui/material/Divider";
// Material UI
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";

import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Box, Link, ListItemButton, ListItemIcon, ListItemText } from "@mui/material";
import { Methods } from "api";
import { useAPI, useAuth } from "hooks";
import { useNavigateRef } from "hooks/useNavigateRef";
import { HOME } from "layout/routes/RouteConstants";
import type { ClaimCountsResponse } from "pages/Homepage/Homepage";
import { useLocation } from "react-router-dom";
import config from "./config";
// Custom
import { useStyles } from "./styles";

/**
 * SideMenu component that displays navigation links and claim counts.
 * It periodically fetches counts to provide real-time updates for each section.
 */
const SideMenu: React.FC = () => {
  const classes = useStyles();
  const navigate = useNavigateRef();
  const location = useLocation();
  const user = useAuth();

  const [menuName, setMenuName] = useState("Home");
  const [selectedMenu, setSelectedMenu] = useState("Home");

  const handleToggleOpen = useCallback((e: string) => {
    setMenuName(e);
  }, []);

  // Standardize the location to set the initial menu state
  useEffect(() => {
    if (location?.pathname.includes("/signin") || location?.pathname.toString() === "/") {
      setMenuName("Home");
      navigate.current(HOME);
    }
  }, [location, navigate]);

  useEffect(() => {
    const selectedItem = config?.find((i) => location.pathname.toString().includes(i.href));
    if (!selectedItem) {
      const fallbackMenu = config?.find((i) => location.pathname.toString().includes(i.href));
      setSelectedMenu(fallbackMenu?.name ?? "Home");
      setMenuName(fallbackMenu?.name ?? "Home");
      return;
    }
    setSelectedMenu(selectedItem?.name ?? "Home");
    setMenuName(selectedItem?.name ?? "Home");
  }, [location.pathname, selectedMenu]);

  // Fetches claim counts for badges
  const { data, trigger: getClaimCounts } = useAPI<ClaimCountsResponse | null, { id: string }>({
    method: Methods.GET,
    fieldName: "getClaimCounts",
    manual: true,
  });

  const fetchCounts = useCallback(() => {
    if (user.user?.username) {
      getClaimCounts({ id: user.user?.username });
    }
  }, [user.user?.username, getClaimCounts]);

  // Fetch on mount/user change
  useEffect(() => {
    fetchCounts();
  }, [fetchCounts]);

  // Poll for updates every 10 seconds
  useEffect(() => {
    if (!user.user?.username) return;
    const interval = setInterval(fetchCounts, 10000);
    return () => clearInterval(interval);
  }, [fetchCounts, user.user?.username]);

  // Refresh counts when the tab regains focus
  useEffect(() => {
    window.addEventListener("focus", fetchCounts);
    return () => window.removeEventListener("focus", fetchCounts);
  }, [fetchCounts]);

  // Map config items to counts from data
  const displayConfig = useMemo(() => {
    if (!data) return config.filter((item) => item.name !== "Duplicated");

    return config.flatMap((item) => {
      // Logic for mapping menu names to technical keys from the API
      const key = item.name.toLowerCase().replace(/\s+/g, "_");

      // Handle special case where "Paid" menu maps to "approved_and_paid" in backend
      const snakeKey = key === "paid" ? "approved_and_paid" : key;
      const camelKey = snakeKey.replace(/_([a-z])/g, (g) => g[1].toUpperCase());

      const count = Number(data[snakeKey] ?? data[camelKey] ?? 0);

      if (item.name === "Duplicated" && count <= 0) return [];

      // We only show counts for specific items, excluding "Home" and "Support"
      const shouldShowBadge = !["Home", "Support"].includes(item.name);

      return [
        {
          ...item,
          count: shouldShowBadge ? count : undefined,
        },
      ];
    });
  }, [data]);

  return (
    <div className={classes.root}>
      <Drawer
        className={classes.drawer}
        variant="persistent"
        anchor="left"
        open={true}
        classes={{
          paper: classes.drawerPaper,
        }}
        ModalProps={{ keepMounted: true }}>
        <div data-test-id="side-menu">
          <div className={classes.drawerHeader} />
          <Divider />
          <List>
            {displayConfig?.map((item) => (
              <React.Fragment key={item.testId}>
                <ListItemButton
                  data-test-id={item.testId}
                  onClick={() => {
                    handleToggleOpen(item.name);
                    const isSameMenu = window.location.pathname.includes(item.href);
                    const isInDetails = (location.state as any)?.isInDetailsPage;

                    if (isSameMenu && isInDetails) {
                      navigate.current(item.href, {
                        state: { isSameMenu: true, isInDetailsPage: true },
                      });
                    } else {
                      navigate.current(item.href);
                    }
                  }}
                  component={Link}
                  selected={menuName === item.name}
                  sx={{ mb: 1 }}>
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText
                    sx={menuName === item.name ? { "& .MuiListItemText-secondary": { fontWeight: "bold" } } : {}}
                    secondary={item.name}
                  />
                  {typeof item.count === "number" && item.count >= 0 && (
                    <Box
                      sx={{
                        backgroundColor: "error.main",
                        color: "#fff",
                        borderRadius: "50%",
                        fontSize: "0.75rem",
                        width: 20,
                        height: 20,
                        minWidth: 20,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        ml: 1,
                      }}>
                      {item.count}
                    </Box>
                  )}
                  {item.items &&
                    (menuName === item.name || selectedMenu === item.name ? <ExpandLessIcon /> : <ExpandMoreIcon />)}
                </ListItemButton>
              </React.Fragment>
            ))}
          </List>
        </div>
      </Drawer>
    </div>
  );
};

export default React.memo(SideMenu);
